import { useState } from 'react'

function Star({ className, fill, stroke }: { className?: string; fill?: string; stroke?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill={fill ?? 'none'}
      stroke={stroke ?? 'currentColor'}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  )
}

interface RatingPopupProps {
  scale: number
  labels?: Record<string, string>
  callId?: string
  conversationId?: string
  tenantSlug: string
  authToken?: string
  /**
   * Overrides the built-in POST /client-portal/widget/rating fetch — pass
   * this when the caller has its own endpoint/submission semantics (e.g.
   * the ticket-detail page's POST .../survey, which also needs its own
   * already-rated handling). Rejecting the returned promise triggers the
   * same inline error state the default fetch path uses.
   */
  onSubmit?: (score: number, comment?: string) => Promise<void>
  onSubmitted: () => void
  onDismiss: () => void
}

export function RatingPopup({ scale, callId, conversationId, tenantSlug, authToken, onSubmit, onSubmitted, onDismiss }: RatingPopupProps) {
  const [selected, setSelected] = useState<number | null>(null)
  const [comment, setComment] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function defaultSubmit(score: number, trimmedComment?: string) {
    const res = await fetch(`/api/client-portal/widget/rating?tenantSlug=${encodeURIComponent(tenantSlug)}`, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        ...(authToken ? { authorization: `Bearer ${authToken}` } : {}),
      },
      credentials: 'include',
      body: JSON.stringify({
        ...(callId ? { callId } : { conversationId }),
        score,
        ...(trimmedComment ? { comment: trimmedComment } : {}),
      }),
    })
    if (!res.ok) throw new Error(`rating submit failed: ${res.status}`)
  }

  async function handleSubmit() {
    if (selected == null || submitting) return
    setSubmitting(true)
    setError(null)
    const trimmedComment = comment.trim() || undefined
    try {
      await (onSubmit ?? defaultSubmit)(selected, trimmedComment)
      onSubmitted()
    } catch {
      setError('Could not submit your rating. Please try again.')
      setSubmitting(false)
    }
  }

  return (
    <div className="kc3-rating-popup p-5 bg-white rounded-2xl shadow-md">
      <h3 className="text-sm font-semibold text-gray-900 mb-3">Rate your experience</h3>
      <div className="flex gap-1.5 mb-3" role="group" aria-label="Rating">
        {Array.from({ length: scale }, (_, i) => i + 1).map((n) => (
          <button
            key={n}
            type="button"
            aria-label={`Rate ${n} out of ${scale}`}
            aria-pressed={selected === n}
            onClick={() => setSelected(n)}
            className="p-0.5"
          >
            <Star
              className="h-6 w-6"
              fill={selected != null && n <= selected ? '#f5b301' : 'none'}
              stroke={selected != null && n <= selected ? '#f5b301' : '#9ca3af'}
            />
          </button>
        ))}
      </div>
      <label htmlFor="kc3-rating-comment" className="sr-only">
        Comment
      </label>
      <textarea
        id="kc3-rating-comment"
        placeholder="Tell us more (optional)"
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        rows={2}
        className="w-full resize-none rounded-lg border border-gray-200 p-2 text-sm mb-3"
      />
      {error && <p className="text-xs text-red-600 mb-2">{error}</p>}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={handleSubmit}
          disabled={selected == null || submitting}
          className="flex-1 rounded-xl bg-blue-600 text-white text-sm font-medium py-2 disabled:opacity-50"
        >
          {submitting ? 'Submitting…' : 'Submit'}
        </button>
        <button type="button" onClick={onDismiss} className="text-sm text-gray-500 px-3">
          Maybe later
        </button>
      </div>
    </div>
  )
}
